/**
 * useUser Hook
 * Decodes and manages the authenticated user from JWT token
 * Returns user info extracted from JWT claims
 */

import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';

export const useUser = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      setIsLoading(true);
      setError(null);

      // Get token from cookies
      const token = Cookies.get('userToken');

      if (!token) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      // Decode JWT (JWT format: header.payload.signature)
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }

      // Decode payload (add padding if necessary)
      const payload = parts[1];
      const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      const decodedPayload = JSON.parse(atob(paddedPayload));

      // Extract user info from JWT claims
      const userData = {
        userId: decodedPayload.sub ? parseInt(decodedPayload.sub) : null,
        email: decodedPayload.email || null,
        name: decodedPayload.name || null,
        role: decodedPayload.role || 'Doctor',
        orgId: decodedPayload.orgId ? parseInt(decodedPayload.orgId) : null,
        aud: decodedPayload.aud || null,
        iss: decodedPayload.iss || null,
      };

      setUser(userData);
    } catch (err) {
      console.error('Error decoding user token:', err);
      setError(err);
      setUser(null);
      // Clear invalid token
      Cookies.remove('userToken');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    user,
    userId: user?.userId,
    email: user?.email,
    name: user?.name,
    role: user?.role,
    orgId: user?.orgId,
    isAdmin: user?.role === 'Admin',
    isLoading,
    error,
  };
};

export default useUser;
